import React, { useState } from "react";
import createInstance from "../../../api/axiosinstance";
import Toast from "../../../utils/toast";
import Loader from "../../Loader";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";

const TP_FeedbackForm = ({ isOpen, onClose, interviewId }) => {
  const [formData, setFormData] = useState({
    technicalKnowledge: { notes: "", score: "", comments: "" },
    handsOnSkills: { notes: "", score: "", comments: "" },
    analyticalThinking: { notes: "", score: "", comments: "" },
    communicationSkills: { notes: "", score: "", comments: "" },
    culturalFit: { notes: "", score: "", comments: "" },
    additionalObservations: "",
    decision: "",
    nextRoundRecommendation: "",
    remarksForHR: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const aiDishaApi = createInstance("DISHA");

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [section, field] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [section]: { ...prev[section], [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Calculate average rating
  const calculateAverageRating = () => {
    const scores = [
      parseInt(formData.technicalKnowledge.score) || 0,
      parseInt(formData.handsOnSkills.score) || 0,
      parseInt(formData.analyticalThinking.score) || 0,
      parseInt(formData.communicationSkills.score) || 0,
      parseInt(formData.culturalFit.score) || 0,
    ];
    const valid = scores.filter((s) => s > 0);
    if (!valid.length) return 0;
    return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
  };

  // Submit feedback (corrected endpoint)
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const avgRating = calculateAverageRating();
    if (avgRating === 0) {
      Toast.error("Please provide ratings for at least one category");
      return;
    }
    if (!formData.decision) {
      Toast.error("Please select a decision");
      return;
    }

    const payload = {
      Detail_schema: {
        Technical_knowledge_detail: formData.technicalKnowledge.notes || "N/A",
        Hands_on_skills_detail: formData.handsOnSkills.notes || "N/A",
        Problem_solving_detail: formData.analyticalThinking.notes || "N/A",
        Communication_skills_detail:
          formData.communicationSkills.notes || "N/A",
        Culture_fit_detail: formData.culturalFit.notes || "N/A",
      },
      scorecard: {
        Technical_knowledge: [
          {
            rating: parseInt(formData.technicalKnowledge.score) || 0,
            comment: formData.technicalKnowledge.comments || "",
          },
        ],
        Hands_on_skills: [
          {
            rating: parseInt(formData.handsOnSkills.score) || 0,
            comment: formData.handsOnSkills.comments || "",
          },
        ],
        Problem_solving: [
          {
            rating: parseInt(formData.analyticalThinking.score) || 0,
            comment: formData.analyticalThinking.comments || "",
          },
        ],
        Communication_skills: [
          {
            rating: parseInt(formData.communicationSkills.score) || 0,
            comment: formData.communicationSkills.comments || "",
          },
        ],
        Culture_fit: [
          {
            rating: parseInt(formData.culturalFit.score) || 0,
            comment: formData.culturalFit.comments || "",
          },
        ],
      },
    };

    try {
      setIsSubmitting(true);

      const response = await aiDishaApi.put(
        `/update_third_party_interview_status_and_feedback?interview_id=${interviewId}&decision=${encodeURIComponent(
          formData.decision
        )}&next_round_recommendation=${encodeURIComponent(
          formData.nextRoundRecommendation
        )}&remarks=${encodeURIComponent(formData.remarksForHR)}`,
        payload
      );

      if (response?.data?.message) {
        Toast.success(response.data.message);
      } else {
        Toast.success("Feedback submitted successfully!");
      }

      clearForm();
      onClose();
      console.log("Feedback submitted:", response.data);
    } catch (error) {
      console.error("Error submitting TP feedback:", error);
      if (error.response?.status === 404) {
        Toast.error(
          "API endpoint not found. Please verify base URL configuration."
        );
      } else {
        Toast.error("Failed to submit feedback. Try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const clearForm = () => {
    setFormData({
      technicalKnowledge: { notes: "", score: "", comments: "" },
      handsOnSkills: { notes: "", score: "", comments: "" },
      analyticalThinking: { notes: "", score: "", comments: "" },
      communicationSkills: { notes: "", score: "", comments: "" },
      culturalFit: { notes: "", score: "", comments: "" },
      additionalObservations: "",
      decision: "",
      nextRoundRecommendation: "",
      remarksForHR: "",
    });
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "80%",
    maxWidth: 1000,
    maxHeight: "90vh",
    overflow: "auto",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 1,
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="tp-interview-feedback"
      aria-describedby="tp-interview-feedback-form"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            bgcolor: "#006A71",
            color: "white",
          }}
        >
          <Typography variant="h6">TP Interview Feedback Form</Typography>
          <IconButton sx={{ color: "white" }} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form Body */}
        <Box component="form" onSubmit={handleFormSubmit} sx={{ p: 3 }}>
          {/* Detailed Feedback */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Feedback Summary
              </Typography>
              {[
                { id: "technicalKnowledge", label: "Technical Knowledge" },
                { id: "handsOnSkills", label: "Hands-on Skills" },
                { id: "analyticalThinking", label: "Problem Solving" },
                { id: "communicationSkills", label: "Communication Skills" },
                { id: "culturalFit", label: "Behavioral / Cultural Fit" },
              ].map((item) => (
                <TextField
                  key={item.id}
                  fullWidth
                  name={`${item.id}.notes`}
                  label={item.label}
                  multiline
                  rows={3}
                  value={formData[item.id].notes}
                  onChange={handleInputChange}
                  margin="normal"
                />
              ))}

              <TextField
                fullWidth
                name="additionalObservations"
                label="Additional Observations"
                multiline
                rows={3}
                value={formData.additionalObservations}
                onChange={handleInputChange}
                margin="normal"
              />
            </CardContent>
          </Card>

          {/* Scorecard */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scorecard (1 - Poor, 5 - Excellent)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "grey.100" }}>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell>Comments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      {
                        id: "technicalKnowledge",
                        label: "Technical Knowledge",
                      },
                      { id: "handsOnSkills", label: "Hands-on Skills" },
                      { id: "analyticalThinking", label: "Problem Solving" },
                      {
                        id: "communicationSkills",
                        label: "Communication Skills",
                      },
                      { id: "culturalFit", label: "Cultural Fit" },
                    ].map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.label}</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            name={`${item.id}.score`}
                            value={formData[item.id].score}
                            onChange={handleInputChange}
                            inputProps={{ min: 1, max: 5 }}
                            size="small"
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            name={`${item.id}.comments`}
                            value={formData[item.id].comments}
                            onChange={handleInputChange}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Final Decision */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Final Recommendation
              </Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel id="decision-label">Decision</InputLabel>
                <Select
                  labelId="decision-label"
                  name="decision"
                  value={formData.decision}
                  onChange={handleInputChange}
                  sx={{ color: "black" }}
                >
                  <MenuItem value="" sx={{ color: "black" }}>
                    Select Decision
                  </MenuItem>
                  <MenuItem value="Strong Hire" sx={{ color: "black" }}>
                    Strong Hire
                  </MenuItem>
                  <MenuItem value="Hire" sx={{ color: "black" }}>
                    Hire
                  </MenuItem>
                  <MenuItem value="Borderline" sx={{ color: "black" }}>
                    Borderline
                  </MenuItem>
                  <MenuItem value="No Hire" sx={{ color: "black" }}>
                    No Hire
                  </MenuItem>
                  <MenuItem value="Keep in Pipeline" sx={{ color: "black" }}>
                    Keep in Pipeline
                  </MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                name="nextRoundRecommendation"
                label="Next Round Recommendation"
                value={formData.nextRoundRecommendation}
                onChange={handleInputChange}
                margin="normal"
              />

              <TextField
                fullWidth
                name="remarksForHR"
                label="Remarks for HR / Next Interviewer"
                multiline
                rows={3}
                value={formData.remarksForHR}
                onChange={handleInputChange}
                margin="normal"
              />
            </CardContent>
          </Card>

          {isSubmitting && <Loader />}

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 2 }}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={clearForm}
              disabled={isSubmitting}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon />}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#006A71",
                "&:hover": { backgroundColor: "#033538" },
              }}
            >
              Submit Feedback
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default TP_FeedbackForm;
